import React, { useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Link,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import { useParams } from "react-router-dom";
import apiClient from "../../services/apiClient";

const cardSx = {
  width: "100%",
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 3,
  boxShadow: "0 10px 30px rgba(15, 23, 42, 0.04)",
};

const websiteValue = (website) =>
  typeof website === "string" ? website : website?.url;

const websiteLabel = (website) => {
  const value = websiteValue(website);
  if (!value) return "";
  try {
    return new URL(value).hostname.replace(/^www\./, "");
  } catch {
    return value;
  }
};

const locationLine = (location = {}) => [
  location.address1,
  location.address2,
  location.city,
  location.stateProvince,
  location.postalCode,
  location.country,
].filter(Boolean).join(", ");

const formatPortalDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString(undefined, {
    year: "numeric", month: "short", day: "numeric",
  });
};

const SectionHeading = ({ icon, title, subtitle }) => (
  <Stack direction="row" spacing={1.5} alignItems="flex-start">
    <Box sx={{ color: "primary.main", display: "flex", mt: 0.25 }}>{icon}</Box>
    <Box>
      <Typography variant="subtitle1" fontWeight={750}>{title}</Typography>
      {subtitle ? <Typography variant="body2" color="text.secondary">{subtitle}</Typography> : null}
    </Box>
  </Stack>
);

const EmptyPortalState = ({ children }) => (
  <Box
    sx={{
      border: "1px dashed",
      borderColor: "divider",
      borderRadius: 2.5,
      bgcolor: "action.hover",
      py: 4,
      px: 2,
      textAlign: "center",
    }}
  >
    <Typography variant="body2" color="text.secondary">{children}</Typography>
  </Box>
);

const ProfileHeader = ({ customer }) => {
  const primaryWebsite = (customer.websites || []).find(websiteValue);
  const primaryLocation = customer.locations?.[0];
  return (
    <Paper
      component="header"
      elevation={0}
      sx={{
        width: "100%",
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 3,
        background: "linear-gradient(135deg, #f8fafc 0%, #eef4ff 55%, #f8fafc 100%)",
      }}
    >
      <Box sx={{ height: { xs: 72, md: 150 }, background: "linear-gradient(90deg, #172554, #1d4ed8 55%, #0891b2)" }} />
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={{ xs: 2, sm: 3 }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        sx={{ width: "100%", px: { xs: 2.5, md: 4 }, pb: { xs: 3, md: 4 }, mt: { xs: -4, md: -5 } }}
      >
        <Avatar
          src={typeof customer.logo === "string" ? customer.logo : ""}
          alt={customer.businessName}
          sx={{
            width: { xs: 88, md: 112 },
            height: { xs: 88, md: 112 },
            bgcolor: "background.paper",
            color: "primary.main",
            border: "5px solid",
            borderColor: "background.paper",
            boxShadow: 2,
            fontSize: 38,
          }}
        >
          <BusinessOutlinedIcon fontSize="inherit" />
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1, pt: { sm: 5 } }}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2}>
            <Box>
              <Typography
                variant="h3"
                component="h1"
                fontWeight={800}
                sx={{ pt: "20px", fontSize: "2.5rem" }}
              >
                {customer.businessName}
              </Typography>
              {customer.industry?.length ? (
                <Typography color="text.secondary" sx={{ mt: 0.5 }}>{customer.industry.join(" · ")}</Typography>
              ) : null}
              {primaryWebsite ? (
                <Link href={websiteValue(primaryWebsite)} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, mt: 1 }}>
                  <LanguageOutlinedIcon fontSize="small" /> {websiteLabel(primaryWebsite)}
                </Link>
              ) : null}
              {primaryLocation ? (
                <Typography color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 1 }}>
                  <LocationOnOutlinedIcon fontSize="small" />
                  {[primaryLocation.city, primaryLocation.stateProvince, primaryLocation.country].filter(Boolean).join(", ")}
                </Typography>
              ) : null}
            </Box>
            {customer.portalViewer?.type ? (
              <Chip
                icon={<VerifiedUserOutlinedIcon />}
                color={customer.portalViewer.type === "administrator" ? "primary" : "success"}
                label={customer.portalViewer.type === "administrator" ? "Administrator portal preview" : "Authorized customer contact"}
                sx={{ alignSelf: { xs: "flex-start", md: "center" } }}
              />
            ) : null}
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
};

const CompanyInfoCard = ({ customer }) => {
  const websites = (customer.websites || []).filter(websiteValue);
  const primaryLocation = customer.locations?.[0];
  const hasInformation = websites.length > 0 || customer.industry?.length || customer.email || customer.phone || primaryLocation;
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 2.5 }}>
      <SectionHeading icon={<BusinessOutlinedIcon />} title="Company information" />
      <Divider sx={{ my: 2 }} />
      {hasInformation ? (
        <Stack spacing={2}>
          {customer.email ? (
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <MailOutlineIcon fontSize="small" color="action" />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary">Email</Typography>
                <Link href={`mailto:${customer.email}`} display="block" sx={{ overflowWrap: "anywhere" }}>{customer.email}</Link>
              </Box>
            </Stack>
          ) : null}
          {customer.phone ? (
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <PhoneOutlinedIcon fontSize="small" color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary">Phone</Typography>
                <Link href={`tel:${customer.phone}`} display="block">{customer.phone}</Link>
              </Box>
            </Stack>
          ) : null}
          {websites.map((website) => (
            <Stack key={websiteValue(website)} direction="row" spacing={1.5} alignItems="flex-start">
              <LanguageOutlinedIcon fontSize="small" color="action" />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary">Website</Typography>
                <Link href={websiteValue(website)} target="_blank" rel="noopener noreferrer" display="block" sx={{ overflowWrap: "anywhere" }}>
                  {websiteLabel(website)}
                </Link>
              </Box>
            </Stack>
          ))}
          {customer.industry?.length ? (
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <CategoryOutlinedIcon fontSize="small" color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary">Industry</Typography>
                <Typography variant="body2">{customer.industry.join(", ")}</Typography>
              </Box>
            </Stack>
          ) : null}
          {primaryLocation ? (
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <LocationOnOutlinedIcon fontSize="small" color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary">Primary location</Typography>
                <Typography variant="body2">{locationLine(primaryLocation)}</Typography>
              </Box>
            </Stack>
          ) : null}
        </Stack>
      ) : <EmptyPortalState>Company profile details will appear here when available.</EmptyPortalState>}
    </Paper>
  );
};

const AccountCard = ({ customer }) => (
  <Paper elevation={0} sx={{ ...cardSx, p: 2.5 }}>
    <SectionHeading icon={<VerifiedUserOutlinedIcon />} title="Account access" />
    <Divider sx={{ my: 2 }} />
    {customer.portalViewer?.type === "administrator" ? (
      <Alert severity="info">Viewing as an Elite Core administrator.</Alert>
    ) : customer.isAuthenticatedCustomer ? (
      <Alert severity="success">You are signed in as an authorized contact for this customer.</Alert>
    ) : (
      <Typography variant="body2" color="text.secondary">
        Authenticated customer portal access is required.
      </Typography>
    )}
    {customer.publicVisibility === "unlisted" ? (
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
        This portal is configured as unlisted; authentication is still required.
      </Typography>
    ) : null}
  </Paper>
);

const PortalSection = ({ icon, title, subtitle, emptyMessage, children, hasItems }) => (
  <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2.5, md: 3 } }}>
    <SectionHeading icon={icon} title={title} subtitle={subtitle} />
    <Divider sx={{ my: 2.5 }} />
    {hasItems ? children : <EmptyPortalState>{emptyMessage}</EmptyPortalState>}
  </Paper>
);

const ProjectsContent = ({ projects }) => (
  <Stack spacing={1.5}>
    {projects.map((project) => {
      const relevantDate = formatPortalDate(project.inHandsDate || project.createdAt);
      return (
        <Box key={project.id || project.orderId} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2.5, p: 2 }}>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1.5}>
            <Box sx={{ minWidth: 0 }}>
              <Typography fontWeight={700}>{project.projectName || (project.orderId ? `Project #${project.orderId}` : "Project")}</Typography>
              {project.projectName && project.orderId ? <Typography variant="caption" color="text.secondary">Project #{project.orderId}</Typography> : null}
            </Box>
            {project.subStatus || project.status ? <Chip size="small" label={project.subStatus || project.status} sx={{ alignSelf: { xs: "flex-start", sm: "center" } }} /> : null}
          </Stack>
          {relevantDate ? (
            <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 1.5 }}>
              <TodayOutlinedIcon fontSize="small" /> {project.inHandsDate ? "Due" : "Created"} {relevantDate}
            </Typography>
          ) : null}
        </Box>
      );
    })}
  </Stack>
);

const ContactsContent = ({ contacts }) => (
  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }, gap: 1.5 }}>
    {contacts.map((contact) => {
      const name = [contact.firstName, contact.lastName].filter(Boolean).join(" ");
      return (
        <Box key={contact.contactId || contact.email} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2.5, p: 2 }}>
          {name ? <Typography fontWeight={700}>{name}</Typography> : null}
          {contact.email ? <Link href={`mailto:${contact.email}`} display="block" sx={{ mt: name ? 0.75 : 0, overflowWrap: "anywhere" }}>{contact.email}</Link> : null}
          {contact.phone ? <Link href={`tel:${contact.phone}`} display="block" color="text.secondary" sx={{ mt: 0.5 }}>{contact.phone}</Link> : null}
        </Box>
      );
    })}
  </Box>
);

const LocationsContent = ({ locations }) => (
  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }, gap: 1.5 }}>
    {locations.map((location, index) => (
      <Box key={location.id || `${location.address1}-${index}`} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2.5, p: 2 }}>
        <Typography fontWeight={700}>{location.label || `Location ${index + 1}`}</Typography>
        {location.type ? <Typography variant="caption" color="text.secondary">{location.type}</Typography> : null}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{locationLine(location)}</Typography>
      </Box>
    ))}
  </Box>
);

const PublicCustomerPage = () => {
  const { publicSlug } = useParams();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    apiClient.get(`${process.env.REACT_APP_NODEJS_SERVER_URL}/customers/public/${encodeURIComponent(publicSlug)}`)
      .then(({ data }) => { if (active) setCustomer(data?.customer || null); })
      .catch((requestError) => {
        if (active) setError(requestError?.response?.status === 404 ? "Customer page not found." : "Unable to load this customer page.");
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [publicSlug]);

  if (loading) {
    return (
      <Box sx={{ width: "100%", minHeight: "70vh", display: "grid", placeItems: "center", px: { xs: 2, md: 4 } }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !customer) {
    return (
      <Box sx={{ width: "100%", px: { xs: 2, md: 4 }, py: 10 }}>
        <Alert severity="info">{error || "Customer page not found."}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", minHeight: "100vh", bgcolor: "#f6f8fb", pb: { xs: 4, md: 6 } }}>
      <Box sx={{ width: "100%", px: { xs: 2, md: 4 }, pt: { xs: 2, md: 4 } }}>
        <ProfileHeader customer={customer} />
      </Box>
      <Box
        component="main"
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "340px minmax(0, 1fr)" },
          gap: 3,
          width: "100%",
          p: { xs: 2, md: 4 },
        }}
      >
        <Stack component="aside" spacing={3} sx={{ width: "100%", minWidth: 0 }}>
          <CompanyInfoCard customer={customer} />
          <AccountCard customer={customer} />
        </Stack>
        <Stack spacing={3} sx={{ width: "100%", minWidth: 0 }}>
          <PortalSection
            icon={<FolderOutlinedIcon />}
            title="Projects"
            subtitle="Customer-visible projects and updates"
            emptyMessage="No projects are available to display yet."
            hasItems={Boolean(customer.projects?.length)}
          >
            <ProjectsContent projects={customer.projects || []} />
          </PortalSection>
          <PortalSection
            icon={<PeopleOutlineIcon />}
            title="Contacts"
            subtitle="Customer-facing contacts"
            emptyMessage="No customer-facing contacts are available to display yet."
            hasItems={Boolean(customer.contacts?.length)}
          >
            <ContactsContent contacts={customer.contacts || []} />
          </PortalSection>
          <PortalSection
            icon={<LocationOnOutlinedIcon />}
            title="Locations"
            subtitle="Customer-visible addresses and locations"
            emptyMessage="No locations are available to display yet."
            hasItems={Boolean(customer.locations?.length)}
          >
            <LocationsContent locations={customer.locations || []} />
          </PortalSection>
        </Stack>
      </Box>
    </Box>
  );
};

export default PublicCustomerPage;
